import type { PerformanceResourceItem } from '@/app/lib/api/types-api'
import { isEventPast } from '@/app/lib/dates'
import { isSaleEnded } from '@/app/lib/purchaseState'

/** Campos del recurso que deciden el aviso. Se pide solo lo que se lee para que
 * el predicado sea usable con un evento parcial en test. */
type UnavailableInput = Pick<
  PerformanceResourceItem,
  'is_cancelled' | 'sale_end' | 'start_at' | 'end_at' | 'date_not_visible'
>

/**
 * ¿Hay que avisar de que el evento ya no se puede comprar?
 *
 * Tres condiciones, cualquiera basta y todas comparten el mismo aviso:
 * cancelado, ventana de venta cerrada, o fecha de celebración ya pasada.
 *
 * **No se lee `purchaseState.kind`** aunque la plantilla ya lo tenga resuelto:
 * `PurchaseState` colapsa a un único estado ganador por precedencia
 * (specs/019-estados-compra-eventos), así que un evento con `purchase_text` del
 * CMS y la venta ya cerrada devolvería `kind: 'custom'` y el cierre de venta
 * quedaría enmascarado. Evaluar cada condición por separado es lo que evita ese
 * falso negativo. Con `is_cancelled` no haría falta (es la primera rama de la
 * cadena y siempre gana), pero se lee del recurso por coherencia: así el aviso
 * no depende de un orden de precedencia que podría reordenarse.
 *
 * `date_not_visible` solo neutraliza la rama de la fecha de celebración (ver
 * `isEventPast`); cancelado y venta cerrada siguen aplicando con él presente.
 */
export function isPerformanceUnavailable(resource: UnavailableInput, now?: Date): boolean {
  if (resource.is_cancelled) return true
  if (isSaleEnded(resource.sale_end, now)) return true

  return isEventPast({
    start: resource.start_at,
    end: resource.end_at,
    dateNotVisible: resource.date_not_visible,
    now,
  })
}
